// src/components/features/Cars/CarFiltersToolbar/CarFiltersToolbar.tsx
//
// Standalone toolbar for the Cars page.
//
// Unlike expense-based pages, the Cars page does NOT use EntityFiltersContext
// or ExpenseFiltersToolbar. It only needs two filters:
//   1. Search keyword (debounced via SearchInput)
//   2. Car status (via drawer with CarStatusSelector)
//
// Desktop: [Add] [Refresh]  ...  [Search] [Filters button]
//          Status chip below (when active)
//          — sticky via AppToolbar's filterChips prop → StickyBox internally
//
// Mobile:  [Search] [Filters button]
//          Status chip below (when active)
//          — sticky via StickyBox
//
// onFiltersChange emit points:
//   1. Initial mount — single emit with empty/default filters
//   2. Search keyword change — debounced via SearchInput, emits immediately
//      when the debounced value arrives
//   3. Drawer Apply — commits draft status, emits combined filters
//   4. Chip removal — clears status, emits updated filters

import React, { useCallback, useMemo, useRef, useState, useEffect } from "react";
import {
  Group,
  Stack,
  Badge,
  CloseButton,
  ScrollArea,
  Box,
  Button,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useTranslation } from "react-i18next";

import { ENTITIES, OPERATION_RIGHTS } from "@/config";
import { CarFilter } from "@/utils/api";
import { useApp, useAppEnvironment } from "@/contexts";
import { useEntityPageShortcuts } from "@/hooks";
import {
  CAR_STATUS_OPTIONS,
  getCarStatusColor,
} from "@/config/carStatuses";

import {
  AppButton,
  AppToolbar,
  SearchInput,
  AppDrawer,
  StickyBox,
} from "@/components/generic";
import { CarStatusSelector } from "@/components/features";

// ============================================
// Types
// ============================================

export interface CarFiltersToolbarProps {
  /** Handler for add button click */
  onAdd?: () => void;

  /** Handler for refresh button click */
  onRefresh?: () => void;

  /** Whether add button is disabled */
  addDisabled?: boolean;

  /** Whether refresh button is disabled */
  refreshDisabled?: boolean;

  /** Whether data is loading */
  loading?: boolean;

  /** Custom label for add button */
  addLabel?: string;

  /** Placeholder text for search input */
  searchPlaceholder?: string;

  /** Enable keyboard shortcuts (default: true) */
  enableShortcuts?: boolean;

  /**
   * Callback emitting a partial CarFilter built from search + status.
   * Emits on: initial mount, debounced search change, drawer Apply, chip removal.
   */
  onFiltersChange?: (filters: Partial<CarFilter>) => void;
}

// ============================================
// Helper: Build CarFilter from local state
// ============================================

function buildCarFilter(
  searchKeyword: string,
  status: number[] | null
): Partial<CarFilter> {
  const filter: Partial<CarFilter> = {};

  const trimmed = searchKeyword.trim();
  if (trimmed) {
    filter.searchKeyword = trimmed;
  }

  if (status && status.length > 0) {
    filter.status = status;
  }

  return filter;
}

// ============================================
// CarFiltersToolbar Component
// ============================================

const CarFiltersToolbarComponent: React.FC<CarFiltersToolbarProps> = ({
  onAdd,
  onRefresh,
  addDisabled = false,
  refreshDisabled = false,
  loading = false,
  addLabel,
  searchPlaceholder,
  enableShortcuts = true,
  onFiltersChange,
}) => {
  const { t } = useTranslation(["cars", "common"]);
  const { hasRightTo } = useApp();
  const { isMobile } = useAppEnvironment();

  const canCreate = hasRightTo(ENTITIES.CARS, OPERATION_RIGHTS.CREATE);

  // ============================================
  // Filter State
  // ============================================

  const [searchKeyword, setSearchKeyword] = useState("");
  const searchKeywordRef = useRef("");

  const [appliedStatus, setAppliedStatus] = useState<number[] | null>(null);
  const appliedStatusRef = useRef<number[] | null>(null);

  const [draftStatus, setDraftStatus] = useState<string | null>(null);

  const [drawerOpened, { open: openDrawer, close: closeDrawer }] =
    useDisclosure(false);

  // ============================================
  // Emit helper
  // ============================================

  const emit = useCallback(
    (keyword: string, status: number[] | null) => {
      if (!onFiltersChange) return;
      onFiltersChange(buildCarFilter(keyword, status));
    },
    [onFiltersChange]
  );

  // ============================================
  // Initial emit on mount
  // ============================================

  const hasEmittedInitialRef = useRef(false);

  useEffect(() => {
    if (!onFiltersChange || hasEmittedInitialRef.current) return;
    hasEmittedInitialRef.current = true;
    emit(searchKeywordRef.current, appliedStatusRef.current);
  }, [onFiltersChange, emit]);

  // ============================================
  // Search handler
  // ============================================

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchKeyword(value);
      searchKeywordRef.current = value;
      emit(value, appliedStatusRef.current);
    },
    [emit]
  );

  // ============================================
  // Drawer handlers
  // ============================================

  const handleDrawerOpen = useCallback(() => {
    if (appliedStatus && appliedStatus.length > 0) {
      setDraftStatus(String(appliedStatus[0]));
    } else {
      setDraftStatus(null);
    }
    openDrawer();
  }, [appliedStatus, openDrawer]);

  const handleDraftStatusChange = useCallback(
    (_name: string, value: string | null) => {
      setDraftStatus(value);
    },
    []
  );

  const handleDrawerApply = useCallback(() => {
    const newStatus = draftStatus !== null ? [Number(draftStatus)] : null;
    setAppliedStatus(newStatus);
    appliedStatusRef.current = newStatus;
    closeDrawer();
    emit(searchKeywordRef.current, newStatus);
  }, [draftStatus, closeDrawer, emit]);

  const handleDrawerClear = useCallback(() => {
    setDraftStatus(null);
  }, []);

  // ============================================
  // Chip removal
  // ============================================

  const handleRemoveStatus = useCallback(() => {
    setAppliedStatus(null);
    appliedStatusRef.current = null;
    emit(searchKeywordRef.current, null);
  }, [emit]);

  // ============================================
  // Active filter count
  // ============================================

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (appliedStatus && appliedStatus.length > 0) count++;
    return count;
  }, [appliedStatus]);

  // ============================================
  // Keyboard Shortcuts
  // ============================================

  useEntityPageShortcuts({
    onAdd: canCreate && !addDisabled && onAdd ? onAdd : undefined,
    onRefresh: !refreshDisabled && onRefresh ? onRefresh : undefined,
    canAdd: canCreate && !addDisabled && !loading,
    canRefresh: !refreshDisabled && !loading,
    enabled: enableShortcuts,
  });

  // ============================================
  // Drawer actions
  // ============================================

  const draftHasFilters = draftStatus !== null;

  const drawerActions = useMemo(
    () => (
      <Group gap="sm" grow>
        {draftHasFilters && (
          <Button variant="default" size="lg" onClick={handleDrawerClear}>
            {t("common:buttons.clear")}
          </Button>
        )}
        <Button size="lg" onClick={handleDrawerApply}>
          {t("common:buttons.apply")}
        </Button>
      </Group>
    ),
    [draftHasFilters, handleDrawerClear, handleDrawerApply, t]
  );

  // ============================================
  // Shared UI elements
  // ============================================

  const searchInput = useMemo(
    () => (
      <SearchInput
        key="search"
        placeholder={
          searchPlaceholder ||
          t("cars:list.searchPlaceholder", { defaultValue: "Search vehicles..." })
        }
        value={searchKeyword}
        onChange={handleSearchChange}
        style={{ minWidth: 200, flex: isMobile ? 1 : undefined }}
      />
    ),
    [searchPlaceholder, searchKeyword, handleSearchChange, isMobile, t]
  );

  const filtersButton = useMemo(
    () => (
      <AppButton
        key="filters"
        variant={activeFilterCount > 0 ? "primary" : "secondary"}
        iconName="Filter"
        onClick={handleDrawerOpen}
      >
        {t("common:buttons.filters", { defaultValue: "Filters" })}
        {activeFilterCount > 0 && ` (${activeFilterCount})`}
      </AppButton>
    ),
    [activeFilterCount, handleDrawerOpen, t]
  );

  const statusChip = useMemo(() => {
    if (!appliedStatus || appliedStatus.length === 0) return null;

    const statusValue = String(appliedStatus[0]);
    const option = CAR_STATUS_OPTIONS.find((opt) => opt.value === statusValue);
    if (!option) return null;

    const color = getCarStatusColor(statusValue);

    return (
      <ScrollArea type="never" scrollbarSize={0}>
        <Group gap="xs" wrap="nowrap">
          <Badge
            variant="light"
            color={color}
            size="lg"
            radius="sm"
            pr={3}
            style={{ flexShrink: 0 }}
            rightSection={
              <CloseButton
                size="xs"
                variant="transparent"
                onClick={handleRemoveStatus}
                aria-label={t("common:buttons.clear")}
              />
            }
          >
            {t(`common:${option.labelKey}`)}
          </Badge>
        </Group>
      </ScrollArea>
    );
  }, [appliedStatus, handleRemoveStatus, t]);

  const actionItems = useMemo(() => {
    const items: React.ReactNode[] = [];
    if (onAdd) {
      items.push(
        <AppButton
          key="add"
          iconName="Plus"
          onClick={onAdd}
          disabled={addDisabled || loading || !canCreate}
        >
          {addLabel || t("common:buttons.add")}
        </AppButton>
      );
    }
    if (onRefresh) {
      items.push(
        <AppButton
          key="refresh"
          variant="secondary"
          iconName="Refresh"
          onClick={onRefresh}
          disabled={refreshDisabled || loading}
          loading={loading}
        />
      );
    }
    return items;
  }, [onAdd, onRefresh, addDisabled, refreshDisabled, loading, canCreate, addLabel, t]);

  const filtersDrawer = useMemo(
    () => (
      <AppDrawer
        opened={drawerOpened}
        onClose={closeDrawer}
        title={t("common:buttons.filters", { defaultValue: "Filters" })}
        position="right"
        size="md"
        responsive
        mobileHeightPercent={60}
        actionType="custom"
        customActions={drawerActions}
        isDirty={false}
      >
        <Stack gap="lg">
          <Box>
            <CarStatusSelector
              name="carStatusFilter"
              value={draftStatus}
              onChange={handleDraftStatusChange}
              label={t("common:selectors.carStatus.label", {
                defaultValue: "Status",
              })}
            />
          </Box>
        </Stack>
      </AppDrawer>
    ),
    [drawerOpened, closeDrawer, drawerActions, draftStatus, handleDraftStatusChange, t]
  );

  // ============================================
  // Layout
  // ============================================

  if (isMobile) {
    return (
      <>
        <StickyBox>
          <Stack gap="xs">
            <Group gap="xs" wrap="nowrap">
              {searchInput}
              {filtersButton}
            </Group>
            {statusChip}
          </Stack>
        </StickyBox>
        {filtersDrawer}
      </>
    );
  }

  return (
    <>
      <AppToolbar
        preset="page-header"
        sections={[
          { id: "actions", items: actionItems, align: "left", gap: "sm" },
          { id: "filters", items: [searchInput, filtersButton], align: "right", gap: "sm" },
        ]}
        filterChips={statusChip}
      />
      {filtersDrawer}
    </>
  );
};

export const CarFiltersToolbar = React.memo(CarFiltersToolbarComponent);